import React, { useMemo, useState, useEffect } from "react";
import Table from "../common/Table";
import PageHeader from "../common/PageHeader";
import fetchData from "../../api/fetchApi";
import { Link, useNavigate } from "react-router-dom";

export default function LawsPage() {
  // Filter states
  const [filterQ, setFilterQ] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterPublishedDate, setFilterPublishedDate] = useState("");
  const [filterLawNumber, setFilterLawNumber] = useState("");
  const dir = "rtl";
  const navigate = useNavigate();

  // Dropdown menu state
  
  // Totals + filters (static demo values)
  const [total, setTotal] = useState(0);
  const [dateRange] = useState({ from: "يناير 2025", to: "يونيو 2025" });

  // API stats state (new API shape includes stats and total)
  const [apiStats, setApiStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Mini stats cards (derived from API stats when available)
  const miniStats = useMemo(() => {
    const s = apiStats || {};
    return [
      {
        title: "تم الإضافة",
        value: s.new_this_month ?? 0,
        delta: s.new_change_pct ?? 0,
        iconBg: "#059669",
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 6L9 17l-5-5"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      },
      {
        title: "تحديثات القوانين",
        value: s.updated_this_month ?? 0,
        delta: s.updated_change_pct ?? 0,
        iconBg: "#B45309",
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 12a8 8 0 1 1 8 8"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M4 12h5"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ),
      },
      {
        title: "المجموع الكلي",
        value: s.total_laws,
        delta: s.new_this_month,
        iconBg: "#6D28D9",
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect
              x="5"
              y="3"
              width="14"
              height="18"
              rx="2"
              stroke="#ffffff"
              strokeWidth="2"
            />
            <line
              x1="9"
              y1="8"
              x2="15"
              y2="8"
              stroke="#ffffff"
              strokeWidth="2"
            />
            <line
              x1="9"
              y1="12"
              x2="15"
              y2="12"
              stroke="#ffffff"
              strokeWidth="2"
            />
            <line
              x1="9"
              y1="16"
              x2="13"
              y2="16"
              stroke="#ffffff"
              strokeWidth="2"
            />
          </svg>
        ),
      },
    ];
  }, [apiStats, total]);

  // Server data + pagination state
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [last, setLast] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  function normalizeLaw(item) {
    const publishRaw =
      item.published_date ||
      item.publish_date ||
      item.published_at ||
      item.created_at ||
      "";
    // If publishRaw looks like an ISO date, format; otherwise display as-is (covers numeric codes like "984")
    let formattedDate = "—";
    if (publishRaw) {
      // try ISO parse
      const d = new Date(publishRaw);
      if (
        !Number.isNaN(d.getTime()) &&
        /\d{4}-\d{2}-\d{2}/.test(String(publishRaw))
      ) {
        formattedDate = d.toLocaleDateString("ar-LY");
      } else {
        formattedDate = String(publishRaw);
      }
    }

    return {
      id: item.uuid || item.id,
      uuid: item.uuid || item.id,
      title: item.title || "—",
      content: item.content || "--",
      // human-friendly display value
      publishDate: formattedDate,
      // raw value from API (if needed)
      published_date: publishRaw,
      // status/type/category
      status: item.status || item.status || item.status || "—",
      // document/reference
      documentNo: item.reference_number || item.document_no || "—",
      // ensure consistent keys used elsewhere in the app
      journal_number: item.journal_number || item.journalNo || "—",
      article_number: item.article_number || item.articleNo || "—",
      law_number: item.law_number || item.lawNo || "—",
      thumb: item.cover || item.image_url || "/3.png",
      category: item.category || item.category || null,
      _raw: item,
    };
  }

  async function loadLaws(p = page, pp = perPage) {
    let items = [];
    try {
      setLoading(true);
      setError(null);
      // Build query params from filters
      const params = [
        `page=${p}`,
        `per_page=${pp}`,
        filterQ ? `q=${encodeURIComponent(filterQ)}` : "",
        filterCategory ? `category_uuids=${encodeURIComponent(filterCategory)}` : "",
        filterPublishedDate ? `published_date=${encodeURIComponent(filterPublishedDate)}` : "",
        filterLawNumber ? `law_number=${encodeURIComponent(filterLawNumber)}` : "",
      ].filter(Boolean).join("&");
      const res = await fetchData(`admin/laws?${params}`);
   
      items = res?.items || res?.data || res?.laws || [];
      setRows(items.map(normalizeLaw));
      const meta = res?.meta || {};
      const current = res?.page ?? meta.current_page ?? res.current_page ?? p;
      const per = res?.per_page ?? pp;
      const total = res?.total ?? meta.total;
      setTotal(total);
      const lastPage =
        meta.last_page ||
        res.last_page ||
        (typeof total === "number" ? Math.max(1, Math.ceil(total / per)) : 1);
      setPage(current);
      setLast(lastPage);
    } catch (e) {
      setError(e?.message || "فشل في جلب البيانات");
    } finally {
      setLoading(false);
    }

    return items;
  }

  // Fetch aggregated stats for the mini cards from the dedicated endpoint
  async function loadStats() {
    try {
      setStatsLoading(true);
      const res = await fetchData("admin/laws/stats");
      // API may return the stats object directly or under data; accept both
      const stats = res?.data ?? res ?? null;
      setApiStats(stats);
    } catch (e) {
      // don't fail the whole page if stats endpoint errors; keep previous stats
      console.error("Failed to load stats:", e);
    } finally {
      setStatsLoading(false);
    }
  }

  useEffect(() => {
    // Load list + stats whenever page/perPage or any filter changes
    loadLaws(page, perPage);
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filterQ, filterCategory, filterPublishedDate, filterLawNumber]);

  function getPages(current, last) {
    if (last <= 3) return Array.from({ length: last }, (_, i) => i + 1);
    if (current === 1) return [1, 2, 3];
    if (current === last) return [last - 2, last - 1, last];
    return [current - 1, current, current + 1];
  }

  // Toggle status API call
  async function handleToggleStatus(row) {
    try {
      setLoading(true);
      await fetchData(`admin/law/status?uuid=${row.uuid}`, "PUT");
      await loadLaws(page, perPage);
      // refresh stats after status change
      loadStats();
    } catch (e) {
      setError(e?.message || "فشل في تحديث الحالة");
    } finally {
      setLoading(false);
    }
  }

  // Delete a law after confirmation. Accepts the entire row object.
  async function handleDeleteRow(row) {
    if (!row || !row.uuid) return;
    const ok = window.confirm("هل أنت متأكد من حذف هذا القانون؟ هذه العملية لا يمكن التراجع عنها.");
    if (!ok) return;

    try {
      setLoading(true);
      setError(null);
      // API: DELETE admin/law?uuid={uuid}
      await fetchData(`admin/law?uuid=${row.uuid}`, "DELETE");
      // refresh list
      const items = await loadLaws(page, perPage);
      // refresh stats after deletion
      loadStats();
      // if the current page became empty after deletion, go back one page
      if ((items?.length ?? 0) === 0 && page > 1) {
        setPage(page - 1);
      }
    } catch (e) {
      setError(e?.message || "فشل في حذف القانون");
    } finally {
      setLoading(false);
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "عنوان القانون",
        accessor: "title",
        className: "p-3",
        render: (r) => (
          <div className="flex items-center gap-3">
            <img
              src={r.thumb}
              alt="thumb"
              className="w-10 h-10 rounded-lg object-cover"
            />
            <div className="truncate max-w-[520px] text-[#1D2035]">
              {r.title}
            </div>
          </div>
        ),
      },
      { header: "رقم المادة", accessor: "law_number" },
      { header: "رقم الجريدة", accessor: "journal_number" },
      { header: "رقم المستند", accessor: "documentNo" },
      { header: "تاريخ النشر", accessor: "publishDate" },
      { header: "نوع القانون", accessor: "category" },
      { header: "حالة القانون", accessor: "status" },

      // { header: "رقم القانون", accessor: "lawNo" },
    ],
    []
  );

  return (
    <div className="min-h-screen p-4 md:p-6 bg-white" dir={dir}>
      <PageHeader title="إجمالي القوانين" total={total} stats={miniStats}>
        <button className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1D2035] flex items-center gap-2">
          <span className="inline-block rotate-180">↕</span>
          من الاحدث إلى الأقدم
        </button>
        <button className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-[#1D2035] flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="#6b7280" />
            <line x1="8" y1="2" x2="8" y2="6" stroke="#6b7280" />
            <line x1="16" y1="2" x2="16" y2="6" stroke="#6b7280" />
            <line x1="3" y1="10" x2="21" y2="10" stroke="#6b7280" />
          </svg>
          {dateRange.from} · {dateRange.to}
        </button>
        <Link
          to="/admin/laws/new"
          className="px-4 py-2.5 rounded-lg text-white bg-[var(--Primary-Primary)] shadow-sm text-sm md:text-base"
        >
          إضافة قانون جديد
        </Link>
      </PageHeader>

      {/* Filters UI */}
      <form
        className="mb-6 flex flex-wrap gap-4 items-end bg-gray-50 p-4 rounded-lg border"
        onSubmit={e => {
          e.preventDefault();
          setPage(1); // reset to first page on filter submit
          loadLaws(1, perPage);
        }}
      >
        <div className="flex flex-col">
          <label className="mb-1 text-sm text-gray-700">بحث نصي</label>
          <input
            type="text"
            className="border rounded px-2 py-1"
            value={filterQ}
            onChange={e => setFilterQ(e.target.value)}
            placeholder="بحث..."
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-sm text-gray-700">رقم القانون</label>
          <input
            type="text"
            className="border rounded px-2 py-1"
            value={filterLawNumber}
            onChange={e => setFilterLawNumber(e.target.value)}
            placeholder="2342"
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-sm text-gray-700">تاريخ النشر</label>
          <input
            type="text"
            className="border rounded px-2 py-1"
            value={filterPublishedDate}
            onChange={e => setFilterPublishedDate(e.target.value)}
            placeholder="مثال: 19 أو 2025-01-01"
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-sm text-gray-700">التصنيف</label>
          <input
            type="text"
            className="border rounded px-2 py-1"
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            placeholder="معرف التصنيف"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded bg-blue-600 text-white text-sm shadow"
        >
          تطبيق الفلاتر
        </button>
        <button
          type="button"
          className="px-4 py-2 rounded bg-gray-300 text-gray-800 text-sm shadow"
          onClick={() => {
            setFilterQ("");
            setFilterLawNumber("");
            setFilterPublishedDate("");
            setFilterCategory("");
            setPage(1);
          }}
        >
          إعادة تعيين
        </button>
      </form>

      {/* Table card */}
      <Table
        columns={columns}
        rows={rows}
        actions={[{ label: "تصدير" }, { label: "تصفية" }]}
        pagination={{
          label: `الصفحة ${page} من ${last}`,
          pages: getPages(page, last),
          current: page,
          last,
          perPage,
          onPageChange: (n) => {
            if (n >= 1 && n <= last) setPage(n);
          },
          onPerPageChange: (n) => {
            setPerPage(n);
            setPage(1);
          },
          // onRowClick: (r) => navigate(`/laws/${r.uuid || r.id}`),
          // onEdit: handleEdit,
          // onStopPublish: handleStopPublish,
          // onDelete: handleDelete,
        }}
        act={[
          {
            label: (
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.33341 2.66646H4.80008C4.05334 2.66646 3.67998 2.66646 3.39476 2.81179C3.14388 2.93962 2.9399 3.14359 2.81207 3.39448C2.66675 3.67969 2.66675 4.05306 2.66675 4.7998V11.1998C2.66675 11.9465 2.66675 12.3199 2.81207 12.6051C2.9399 12.856 3.14388 13.06 3.39476 13.1878C3.67998 13.3331 4.05334 13.3331 4.80008 13.3331H11.2001C11.9468 13.3331 12.3202 13.3331 12.6054 13.1878C12.8563 13.06 13.0603 12.856 13.1881 12.6051C13.3334 12.3199 13.3334 11.9465 13.3334 11.1998V8.33313M10.3334 3.66646L12.219 5.55208M7.17524 6.82464L11.6074 2.39248C12.1281 1.87178 12.9723 1.87178 13.493 2.39248C14.0137 2.91318 14.0137 3.7574 13.493 4.27809L8.91837 8.85274C8.41061 9.3605 8.15673 9.61438 7.86755 9.81629C7.61087 9.9955 7.33403 10.144 7.04273 10.2586C6.71455 10.3878 6.3626 10.4588 5.6587 10.6008L5.33342 10.6665L5.36505 10.445C5.47699 9.66144 5.53296 9.26965 5.66027 8.90387C5.77325 8.5793 5.92758 8.27063 6.11946 7.98551C6.33569 7.66419 6.61554 7.38434 7.17524 6.82464Z"
                    stroke="#1A6AB3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>{" "}
                <p className="text-[#1A6AB3]">تعديل</p>
              </div>
            ),
            onClick: (row) => navigate(`/admin/laws/${row.uuid}`),
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <p className="text-[#15936E]">تعديل الحالة</p>
              </div>
            ),
            onClick: (row) => handleToggleStatus(row),
          },
          {
            label: (
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.6667 3.33325H11.3334L10.6667 2.66659H5.33341L4.66675 3.33325H1.33341M12.6667 3.33325V12.6666C12.6667 13.1199 12.5261 13.5559 12.2192 13.8628C11.9123 14.1697 11.4763 14.3103 11.0231 14.3103H4.97608C4.52285 14.3103 4.08686 14.1697 3.77996 13.8628C
3.47307 13.5559 3.33241 13.1199 3.33241 12.6666V3.33325H12.6667Z"
                    stroke="#E02424"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.66675 7.33325V11.3333"
                    stroke="#E02424"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9.33325 7.33325V11.3333"
                    stroke="#E02424"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <p className="text-[#E02424]">حذف القانون</p>
              </div>
            ),

            onClick: (row) => handleDeleteRow(row),
          },
        ]}
      />

      {loading && (
        <div className="mt-4 text-center text-sm text-gray-500">
          جاري التحميل...
        </div>
      )}
      {error && (
        <div className="mt-2 text-center text-sm text-red-600">{error}</div>
      )}
    </div>
  );
}
